import { Injectable } from '@angular/core';
import { ComponentsToLoad, SubComponentsToLoad } from '../enums/components-to-load.enum';

/**
 * PathRoutingService — Registry of recognized URL path routes.
 *
 * Follows the QuickLinkService static-getter pattern for URL detection.
 * Each recognized path maps to a ComponentsToLoad value (string).
 *
 * SEMANTIC INVARIANT: Only recognized paths trigger path routing.
 * Unrecognized paths MUST fall through to standard behavior.
 *
 * AC1: First recognized path — /assetTag/?AssetTagId=X
 * AC2: Unrecognized paths fall through to standard behavior (handled in caller).
 */
@Injectable({
    providedIn: 'root'
})
export class PathRoutingService {
    /**
     * Registry of recognized path segments to component names.
     * Add new path routes here to extend the registry.
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention
    private static readonly recognizedPaths: Record<string, string> = {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        '/assetTag/': 'AssetTag'
    };

    /** Full current URL including query string */
    static get url(): string {
        return window?.location?.href || '';
    }

    /** Current pathname (no query string) */
    static get pathname(): string {
        return window?.location?.pathname || '';
    }

    /**
     * Returns true if the current URL matches the AssetTag path.
     * Supports both pathname-based (/assetTag/) and query-param-based (?AssetTagId=X) detection.
     */
    static get isAssetTag(): boolean {
        return this.pathname.includes('/assetTag/') || this.url.includes('AssetTagId=');
    }

    /**
     * Returns true if the current URL matches ANY recognized path in the registry.
     * Use this as the primary gate in customer-portal.component.ts.
     */
    static get isPathRoute(): boolean {
        return Object.keys(this.recognizedPaths).some(path => this.pathname.includes(path));
    }

    /**
     * Returns the component name string for the first matching recognized path,
     * or null if no path matches.
     */
    static get matchedComponent(): string | null {
        for (const [path, component] of Object.entries(this.recognizedPaths)) {
            if (this.pathname.includes(path)) {
                return component;
            }
        }
        return null;
    }

    /** Current hash with the leading '#' stripped and the eSign provider's appended marker removed. */
    static get hash(): string {
        // The eSign provider appends &esigDocumentId=<n> to the end of the return URL, landing inside
        // the fragment; drop it so the deep-link parser reads a clean contract id (ADO-129436).
        return (window?.location?.hash || '').replace(/^#/, '').replace(/[?&]esigDocumentId=[^&]*/i, '');
    }

    /** Tabs that can host the ContractDetail sub-state as a deep link */
    private static readonly contractDetailHostTabs: string[] = [ComponentsToLoad.Contracts, ComponentsToLoad.ItemsOut];

    /**
     * True when {tab} can host the ContractDetail sub-state. Single source of truth
     * for both the reader (contractDeepLink) and writers (AppFacadeService.openContract),
     * so the deep-link URL is only ever written for a tab that can restore it on reload.
     */
    static canHostContractDetail(tab: ComponentsToLoad | string | undefined | null): boolean {
        return !!tab && this.contractDetailHostTabs.includes(tab as string);
    }

    /**
     * Parses a plain '#{tab}' hash into a recognized ComponentsToLoad, or null.
     * A hash carrying extra path segments (a deep link) is not a plain route.
     */
    static get hashRoute(): { tab: ComponentsToLoad } | null {
        const hash = this.hash;
        if (!hash || hash.includes('/')) {
            return null;
        }
        return (Object.values(ComponentsToLoad) as string[]).includes(hash) ? { tab: hash as ComponentsToLoad } : null;
    }

    /**
     * Parses a '#{tab}/ContractDetail/{id}' deep link. Only Contracts and ItemsOut
     * are accepted as the host tab; anything else returns null.
     */
    static get contractDeepLink(): { tab: ComponentsToLoad; id: string } | null {
        const [tab, sub, id] = this.hash.split('/');
        if (this.contractDetailHostTabs.includes(tab) && sub === SubComponentsToLoad.ContractDetail && id) {
            return { tab: tab as ComponentsToLoad, id };
        }
        return null;
    }

    /**
     * Reads a query parameter from the current URL's search string.
     * @param key - The query parameter key (case-sensitive)
     * @returns The decoded parameter value, or null if not present
     */
    static getQueryParam(key: string): string | null {
        const params = new URLSearchParams(window?.location?.search || '');
        return params.get(key);
    }

    private static capturedEsignReturn: string | null = null;

    /**
     * The contract id from ?esignReturn=<id> (the eSign provider's return marker), as captured at startup.
     * AppFacadeService.updateLocation rewrites the address bar to '#Tab' and so drops the query; anything
     * that needs the marker later must read this, not the URL.
     */
    static get esignReturnContractId(): string | null {
        return this.capturedEsignReturn;
    }

    /** Capture the marker from the current URL. The first capture wins; later calls (after the query is gone) are no-ops. */
    static captureEsignReturn(): void {
        this.capturedEsignReturn ??= this.getQueryParam('esignReturn');
    }

    static clearEsignReturn(): void {
        this.capturedEsignReturn = null;
    }

    // Only redirect to an https provider URL (http allowed for localhost/127.0.0.1 in local dev).
    // esignUrl is backend-sourced; this blocks a javascript:/data: value from running in our origin.
    static isSafeEsignUrl(url: string): boolean {
        try {
            const { protocol, hostname } = new URL(url);
            return protocol === 'https:' || (protocol === 'http:' && (hostname === 'localhost' || hostname === '127.0.0.1'));
        } catch {
            return false;
        }
    }

    // Local https proxy that 302s to its ?location= target. Lets eSign round-trip in local dev,
    // where the provider rejects a RedirectUrl that is not https or carries a port (localhost:4200).
    private static readonly localEsignRedirectProxy = 'https://cloudrent.local/redirect.php';

    /**
     * The RedirectUrl the eSign provider sends the customer back to. The hash restores the contract detail
     * only on tabs that can host it (same gate as AppFacadeService.openContract).
     */
    static buildEsignRedirectUrl(contractId: string, activeTab?: ComponentsToLoad): string {
        // Preserve the existing query — a QuickLink session's identity lives in ?token= (also ?css=);
        // dropping it detaches the sessionStorage session on return → 401s (ADO-117634).
        // Forwarding ?token= to the eSign provider via RedirectUrl is security-reviewed and accepted
        // (ADO-104812, PR #32194): the provider is a trusted processor.
        const preserved = new URLSearchParams(window.location.search);
        preserved.delete('esignReturn');
        const prefix = preserved.toString();
        const base = `${window.location.origin}${window.location.pathname}?${prefix ? `${prefix}&` : ''}esignReturn=${encodeURIComponent(contractId)}`;
        const full = this.canHostContractDetail(activeTab) ? `${base}#${activeTab}/${SubComponentsToLoad.ContractDetail}/${contractId}` : base;
        return this.wrapForLocalEsignRedirect(full);
    }

    /**
     * In local dev (localhost/127.0.0.1 with a port) the provider rejects the http/ported RedirectUrl,
     * so route it through a local https proxy that redirects back to the target. encodeURIComponent keeps
     * the target's own query and #deep-link intact as a single param across the hop. No-op when deployed.
     */
    private static wrapForLocalEsignRedirect(url: string): string {
        try {
            const { hostname, port } = new URL(window.location.origin);
            const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
            if (isLocalhost && port) {
                return `${this.localEsignRedirectProxy}?location=${encodeURIComponent(url)}`;
            }
        } catch {
            // fall through to the unwrapped url
        }
        return url;
    }
}
